import { Program } from '@citruscamps/citrus-client'
import { faHandHoldingDollar, faHandsHoldingDollar } from '@fortawesome/pro-duotone-svg-icons'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useClipboard } from '../../../hooks/useClipboard'
import { LoadingButton } from '../../LoadingButton/LoadingButton'
import { useFetchCitrusPromotion } from '../hooks/useFetchCitrusPromotions'

interface IPortalProps {
  children: React.ReactNode
}

const Portal = ({ children }: IPortalProps) => {
  return createPortal(children, document.body)
}

interface IProps {
  program: Program
  onClose?: () => any
}

export const ReferralModal = ({ program, onClose: handleClose }: IProps) => {
  const detailRef = useRef<HTMLDivElement>(null)
  const { data: citrusPromo } = useFetchCitrusPromotion({ program })
  const { referral } = citrusPromo || {}
  const referalAmount: string | undefined = referral?.display?.split('.').shift()
  const [isShowingDetails, setIsShowingDetails] = useState<boolean>()
  const { copy: copyToClipboard, isCopied, isCoping } = useClipboard()
  const url = new URL(`${window.location.origin}/signup`)
  url.searchParams.append('utm_source', 'referral')
  url.searchParams.append('utm_medium', 'direct')
  url.searchParams.append('utm_campaign', program.id)

  return (
    <Portal>
      <div className="modal fade show fade-in overlay d-flex">
        <div className="modal-dialog slide-in-blurred-top my-auto">
          <div className="modal-content border-0">
            <div className="modal-header justify-content-end border-0">
              <button type="button" className="btn text-body" onClick={() => handleClose?.()}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body pt-0">
              <h4 className="text-center">Give the gift of Citrus to other programs</h4>
              <div className="text-center">
                You both get a promo when your friend makes their first payment using Citrus.{' '}
                {!isShowingDetails && (
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    style={{ verticalAlign: 'unset' }}
                    onClick={() => {
                      detailRef?.current?.scrollIntoView({ behavior: 'smooth' })
                      setIsShowingDetails(true)
                    }}
                  >
                    See details
                  </button>
                )}
              </div>
              <div className="d-flex" style={{ overflowX: 'hidden' }}>
                <div className="col-12 my-3">
                  <div className="mt-3">
                    <div className="d-flex">
                      <div className="me-3">
                        <FontAwesomeIcon
                          icon={faHandHoldingDollar}
                          className="text-bg-primary rounded p-3"
                          size="xl"
                        />
                      </div>
                      <div className="mt-1">
                        <div className="fw-bold">You get a {referalAmount} credit</div>
                        <div className="text-body-secondary">Applied on your next invoice</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="d-flex">
                      <div className="me-3">
                        <FontAwesomeIcon
                          icon={faHandsHoldingDollar}
                          className="text-bg-primary rounded p-3"
                          size="xl"
                        />
                      </div>
                      <div className="mt-1">
                        <div className="fw-bold">They get a {referalAmount} credit</div>
                        <div className="text-body-secondary">Applied on their next invoice</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 my-3" ref={detailRef}>
                  <p className="small">
                    When your invitee processes a payment using Citrus for the first time, they'll
                    receive the generous credit you gave and you'll receive one for your own future
                    invoice(s). Promo terms such as promo value, number of invoices it applies to,
                    minimum spend required and maximum savings possible may vary by location.
                  </p>
                  <a
                    className="btn btn-outline-primary btn-block mt-2"
                    href="https://www.joincitrus.com/terms-of-service"
                  >
                    See terms
                  </a>
                </div>
              </div>
            </div>
            <div className="modal-footer bg-body-tertiary border-0">
              <div className="d-sm-flex">
                <div className="small">
                  <div className="fw-bold">Invite link to Citrus</div>
                  <div className="text-body-secondary">
                    <b>They will not receive access to {program.name}</b>, only an invitation to
                    setup their program with Citrus.
                  </div>
                </div>
                <div className="d-sm-flex" style={{ minWidth: 120 }}>
                  <div className="mt-3 my-sm-auto">
                    <LoadingButton
                      type="button"
                      className="btn btn-primary btn-block d-sm-inline-block w-sm-auto"
                      onClick={async () => {
                        await copyToClipboard(url.toString())
                      }}
                    >
                      {isCopied && !isCoping ? 'Copied!' : 'Copy link'}
                    </LoadingButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </Portal>
  )
}
